import { useState } from "react";
import { toast } from "sonner";
import { Copy, Gamepad2, KeyRound, Plus, Rocket, UserRound } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useParentScope } from "@/context/ParentContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/States";
import { ContextualNav } from "@/components/ContextualNav";

export default function ParentStudents() {
  const { principal, refresh } = useAuth();
  const { students, reloadStudents, selectStudent } = useParentScope();
  const [form, setForm] = useState({ first_name: "", grade: 4, username: "", pin: "", gaming_mode: true });
  const [showForm, setShowForm] = useState(students.length === 0);
  const [busy, setBusy] = useState(false);
  const [pinEdit, setPinEdit] = useState({});

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/parent/students", { ...form, grade: Number(form.grade) });
      toast.success(`${form.first_name} ya puede entrar con el codigo de familia.`);
      setForm({ first_name: "", grade: 4, username: "", pin: "", gaming_mode: true });
      setShowForm(false);
      await reloadStudents();
    } catch (err) {
      toast.error(apiError(err, "No pudimos crear el perfil."));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body, message) => {
    try {
      await api.patch(`/parent/students/${id}`, body);
      await reloadStudents();
      if (message) toast.success(message);
    } catch (err) {
      toast.error(apiError(err, "No pudimos guardar el cambio."));
    }
  };

  const copyCode = async () => {
    const code = principal?.family_code || (await refresh())?.family_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Codigo copiado");
    } catch {
      toast.info(`Codigo de familia: ${code}`);
    }
  };

  const copyStudentAccess = async (student) => {
    const code = principal?.family_code || (await refresh())?.family_code;
    if (!code) return;
    const instructions = [
      `Acceso de ${student.first_name} a MAIKY`,
      `Página: ${window.location.origin}/estudiante`,
      `Código de familia: ${code}`,
      `Usuario: ${student.username}`,
      "PIN: usa el PIN que configuraste para este perfil.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(instructions);
      toast.success(`Datos de acceso de ${student.first_name} copiados`);
    } catch {
      toast.info(`Código ${code} · Usuario ${student.username}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="parent-students-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Mis hijos</h1>
          <p className="mt-1 text-sm text-slate-500">Cada hijo entra con el codigo de familia, su usuario y su PIN.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={copyCode}
            className="estudia-touch rounded-xl font-semibold"
            data-testid="copy-family-code-btn"
          >
            <Copy className="mr-2 h-4 w-4" />
            Codigo: <span className="ml-1 font-display tracking-widest">{principal?.family_code || "—"}</span>
          </Button>
          <Button
            onClick={() => setShowForm((v) => !v)}
            className="estudia-touch rounded-xl bg-blue-600 font-semibold hover:bg-blue-700"
            data-testid="toggle-student-form-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> Nuevo perfil
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="estudia-card animate-rise space-y-4 p-5" data-testid="student-create-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="first_name">Nombre</Label>
              <Input
                id="first_name"
                required
                className="estudia-touch mt-1.5"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                data-testid="student-name-input"
              />
            </div>
            <div>
              <Label htmlFor="grade">Grado</Label>
              <Input
                id="grade"
                type="number"
                min={1}
                max={12}
                required
                className="estudia-touch mt-1.5"
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                data-testid="student-grade-input"
              />
            </div>
            <div>
              <Label htmlFor="username">Usuario (sin correo)</Label>
              <Input
                id="username"
                required
                minLength={3}
                pattern="[a-zA-Z0-9_.\-]+"
                className="estudia-touch mt-1.5"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                data-testid="student-username-input"
              />
            </div>
            <div>
              <Label htmlFor="pin">PIN (solo numeros)</Label>
              <Input
                id="pin"
                required
                inputMode="numeric"
                minLength={4}
                maxLength={12}
                className="estudia-touch mt-1.5"
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                data-testid="student-pin-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="gaming"
              checked={form.gaming_mode}
              onCheckedChange={(v) => setForm({ ...form, gaming_mode: v })}
              data-testid="student-gaming-switch"
            />
            <Label htmlFor="gaming" className="text-sm">
              Activar Gaming Mode (monedas y premios)
            </Label>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="estudia-touch rounded-xl bg-blue-600 font-bold hover:bg-blue-700"
            data-testid="student-create-submit-btn"
          >
            {busy ? "Creando..." : "Crear perfil"}
          </Button>
        </form>
      )}

      {students.length === 0 && !showForm ? (
        <EmptyState title="Sin perfiles todavia" description="Crea el primer perfil para empezar." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {students.map((student) => (
            <div key={student.id} className="estudia-card p-5" data-testid="student-card">
              <div className="flex items-start gap-4">
                <Avatar config={student.avatar_config} name={student.first_name} size="md" testId="student-card-avatar" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-lg font-bold text-slate-900">{student.first_name}</h3>
                  <p className="text-sm text-slate-500">
                    {student.grade}º grado · usuario <span className="font-semibold">{student.username}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {student.last_login_at
                      ? `Ultimo acceso: ${new Date(student.last_login_at).toLocaleString()}`
                      : "Todavia no ha entrado"}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Gamepad2 className="h-4 w-4 text-amber-500" /> Gaming Mode
                </span>
                <Switch
                  checked={student.gaming_mode}
                  aria-label={`Modo de juego de ${student.first_name}`}
                  onCheckedChange={(v) =>
                    patch(student.id, { gaming_mode: v }, v ? "Gaming Mode activado" : "Gaming Mode desactivado")
                  }
                  data-testid={`gaming-toggle-${student.username}`}
                />
              </div>
              {student.gaming_mode && (
                <p className="mt-2 text-xs text-slate-500" data-testid="student-coins-hint">
                  Saldo actual: <span className="font-bold text-amber-700">{student.coins_balance} monedas</span>
                </p>
              )}
              {!student.gaming_mode && (
                <p className="mt-2 text-xs text-slate-500">
                  El estudio sigue funcionando igual. El saldo ({student.coins_balance}) y el historial se conservan.
                </p>
              )}

              <div className="mt-4 rounded-2xl border-2 border-violet-100 bg-gradient-to-br from-violet-50 to-cyan-50 p-4" data-testid={`student-access-${student.username}`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-violet-600 text-white shadow-md shadow-violet-200">
                    <Rocket className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-extrabold text-slate-900">Acceso del alumno</p>
                    <p className="text-xs text-slate-500">Tres datos para entrar desde cualquier dispositivo.</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">Código familiar</span>
                    <span className="font-display font-extrabold tracking-widest text-violet-700">{principal?.family_code || "—"}</span>
                  </div>
                  <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                    <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">Usuario</span>
                    <span className="flex items-center gap-1 font-bold text-cyan-800"><UserRound className="h-3.5 w-3.5" />{student.username}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="estudia-touch mt-3 w-full rounded-xl border-violet-200 bg-white font-bold text-violet-700 hover:bg-violet-100"
                  onClick={() => copyStudentAccess(student)}
                  data-testid={`copy-student-access-${student.username}`}
                >
                  <Copy className="mr-2 h-4 w-4" /> Copiar instrucciones de acceso
                </Button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="estudia-touch rounded-xl text-sm"
                  onClick={() => selectStudent(student.id)}
                  data-testid={`select-student-${student.username}`}
                >
                  Ver su panel
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Nuevo PIN"
                    inputMode="numeric"
                    className="estudia-touch w-32"
                    value={pinEdit[student.id] || ""}
                    onChange={(e) => setPinEdit({ ...pinEdit, [student.id]: e.target.value.replace(/\D/g, "") })}
                    data-testid={`pin-reset-input-${student.username}`}
                  />
                  <Button
                    variant="outline"
                    aria-label={`Actualizar PIN de ${student.first_name}`}
                    className="estudia-touch rounded-xl"
                    disabled={(pinEdit[student.id] || "").length < 4}
                    onClick={() => {
                      patch(student.id, { pin: pinEdit[student.id] }, "PIN actualizado");
                      setPinEdit({ ...pinEdit, [student.id]: "" });
                    }}
                    data-testid={`pin-reset-btn-${student.username}`}
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {students.length > 0 && (
        <ContextualNav
          title="Continúa con su preparación"
          description="El perfil ya está listo. Ahora conecta sus fechas y apuntes."
          items={[
            { to: "/p/calendario", icon: "calendar", label: "Añadir materias y exámenes", description: "Define qué debe estudiar y para cuándo." },
            { to: "/p/material", icon: "material", label: "Preparar material", description: "Crea ejercicios desde sus apuntes." },
          ]}
        />
      )}
    </div>
  );
}
